/**
 * Verbatim owner-billing copy tables + the honest date formatter (spec §5).
 *
 * Every string here is quoted from the binding spec's copy tables. Honesty is the
 * invariant: `{date}` comes ONLY from a real `trialEndsAt` / `currentPeriodEnd`, and
 * `{n}` only from the server's `daysLeft` — no fabricated urgency, no invented
 * countdown, no discount the server didn't give. Centralizing the copy keeps the
 * strip, the billing page, and the paywall notice byte-identical and testable.
 */

/**
 * Format an ISO date as `MMM D, YYYY` (e.g. "Aug 1, 2026") in UTC, via
 * `Intl.DateTimeFormat`. UTC is deliberate: `trialEndsAt` is a UTC instant and the
 * displayed calendar date must not drift by the viewer's timezone near a boundary.
 * A missing / unparseable date yields an empty string (the caller omits the clause).
 */
export function formatBillingDate(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length === 0) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(ms));
}

/** The trial-strip attention states (the only five states the strip renders). */
export type TrialStripState =
  | 'trialing-comfortable'
  | 'trialing-urgent'
  | 'trialing-final'
  | 'grace'
  | 'lapsed';

/**
 * The final-48h window (spec §4.4 "Trialing, final 48h") in milliseconds. Once the
 * trial ends within this window, Stripe cannot align a checkout to a trial that is
 * about to end (the M1 backend returns `409 owner_billing_trial_alignment_unavailable`
 * in this window, §2.2), so checkout is blocked with the honest reason rather than
 * silently failing on click.
 */
export const FINAL_TRIAL_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Whether a trialing payload is in the final-48h checkout-blocked window: entitled,
 * `trialing`, with a parseable `trialEndsAt` that is in the future but ≤ 48h away.
 * Pure over `{ status, isEntitled, trialEndsAt }` + an injectable `now` (R3 purity):
 * a payload 47h out is final; one 49h out is not. Kept payload-driven (never a click
 * side-effect) so the strip AND the hero can surface the state before any click.
 */
export function isFinalTrialWindow(
  state: OwnerBillingStateResultLike | undefined,
  now: number = Date.now(),
): boolean {
  if (!state || state.status !== 'trialing' || !state.isEntitled) return false;
  if (typeof state.trialEndsAt !== 'string' || state.trialEndsAt.length === 0) {
    return false;
  }
  const endMs = Date.parse(state.trialEndsAt);
  if (!Number.isFinite(endMs)) return false;
  const remaining = endMs - now;
  return remaining > 0 && remaining <= FINAL_TRIAL_WINDOW_MS;
}

/**
 * Map a billing payload to the strip's attention state, or `null` when the strip
 * renders nothing. Pure (payload + injectable `now`) so it is trivially testable and
 * drives the strip deterministically (R3): active / none / undefined → null; a
 * trialing state splits final-48h (checkout-blocked window) → urgent (≤7d) →
 * comfortable (>7d); past-due splits grace (entitled) vs lapsed; canceled → lapsed.
 */
export function resolveStripState(
  state: OwnerBillingStateResultLike | undefined,
  now: number = Date.now(),
): TrialStripState | null {
  if (!state) return null;
  switch (state.status) {
    case 'trialing':
      if (state.isEntitled) {
        if (isFinalTrialWindow(state, now)) return 'trialing-final';
        return state.daysLeft > 7 ? 'trialing-comfortable' : 'trialing-urgent';
      }
      return 'lapsed';
    case 'past_due':
      return state.isEntitled ? 'grace' : 'lapsed';
    case 'canceled':
      return 'lapsed';
    default:
      return null; // active / none never render the strip
  }
}

/** The minimal shape `resolveStripState` / `isFinalTrialWindow` read (no type cycle). */
interface OwnerBillingStateResultLike {
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'none';
  isEntitled: boolean;
  daysLeft: number;
  trialEndsAt?: string | null;
}

/** Verbatim trial-strip copy (spec §5 "Trial strip"). */
export const STRIP_COPY = {
  trialingComfortable: {
    kicker: 'FREE TRIAL',
    body: (n: number) => `Your owner tools are free for ${n} more days.`,
    cta: 'Subscribe',
  },
  trialingUrgent: {
    kicker: 'TRIAL ENDING',
    body: (date: string, n: number) =>
      `Your free trial ends on ${date} — ${n} day${n === 1 ? '' : 's'} left.`,
    cta: 'Subscribe now',
  },
  finalHours: {
    kicker: 'TRIAL ENDING',
    body: (date: string) =>
      `Your trial ends on ${date}. You can subscribe as soon as it ends.`,
    cta: 'Subscribe when trial ends',
  },
  grace: {
    kicker: 'PAYMENT FAILED',
    body: 'We couldn’t process your last payment. Update your payment method to keep your owner tools.',
    cta: 'Update payment',
  },
  lapsed: {
    kicker: 'TOOLS PAUSED',
    body: 'Your owner tools are paused. Reactivate your subscription to generate capital stacks and grant data-room access.',
    cta: 'Reactivate',
  },
} as const;

/** Verbatim billing-page hero copy (spec §5 "Billing page hero"). */
export const HERO_COPY = {
  trialing: {
    title: 'You’re on a free trial',
    body: (date: string) =>
      `Your owner tools are free until ${date}. Subscribe any time to keep them after your trial.`,
    cta: 'Subscribe',
  },
  active: {
    title: 'Subscription active',
    // A renewal date is present on a normal active row; render the "renews on {date}"
    // clause ONLY when we actually have a date. When `currentPeriodEnd` is null
    // (reachable via `applyInvoicePaid` with a null period) we must NOT emit
    // "renews on ." — fall back to the neutral active line (#29).
    body: (date: string) =>
      date
        ? `Your owner tools are active. Your subscription renews on ${date}.`
        : 'Your owner tools are active.',
    cta: 'Manage subscription',
  },
  grace: {
    title: 'Payment needs attention',
    body: 'Your last payment didn’t go through. Update your payment method to avoid losing access.',
    cta: 'Update payment method',
  },
  lapsed: {
    title: 'Subscription ended',
    body: 'Your owner tools are paused. Restart your subscription to generate capital stacks and grant data-room access.',
    cta: 'Restart subscription',
  },
  finalHours: {
    title: 'You’re on a free trial',
    body: (date: string) =>
      `Your trial ends on ${date}. Checkout opens as soon as your trial ends — your owner tools stay free until then.`,
    cta: 'Subscribe when trial ends',
  },
  notConfigured: {
    title: 'Billing isn’t enabled yet',
    body: 'Paid owner tools aren’t available on this environment yet. You can keep managing your listing, editing raise facts, and responding to introductions for free.',
    cta: null,
  },
  transientError: {
    title: 'Couldn’t load billing',
    body: 'We couldn’t reach the billing service. Your listing and free tools are unaffected.',
    cta: 'Retry',
  },
  none: {
    title: 'No billing to manage',
    body: 'You don’t own a verified raise yet. Claim your listing to unlock owner tools and a 30-day free trial.',
    cta: 'Browse the directory',
  },
  confirming: {
    title: 'Confirming your subscription…',
    body: 'Your payment went through. We’re activating your owner tools — this usually takes a few seconds.',
    cta: null,
  },
  // The webhook-lag poll exhausted without seeing `active`. This is NOT a failure —
  // the payment went through; activation is just taking longer than usual. Give the
  // owner an authoritative, non-busy state with a Refresh, NEVER a perpetual spinner
  // (#10).
  confirmationDelayed: {
    title: 'Still activating your subscription',
    body: 'Your payment went through. Activation is taking a little longer than usual — this can happen briefly. Refresh to check again; your owner tools will switch on automatically once it completes.',
    cta: 'Refresh',
  },
  // C23(b) — an EXPIRED trial (server status still `trialing` but `isEntitled: false`).
  // The hero must NOT keep claiming the trial is live ("You’re on a free trial"): the
  // entitlement is gone, so it reads as ended with a subscribe affordance. Derived from
  // `isEntitled`, never from the raw status alone.
  trialEnded: {
    title: 'Your free trial has ended',
    body: 'Your 30-day free trial is over. Subscribe to keep generating capital stacks, granting data-room access, and publishing to investors.',
    cta: 'Subscribe',
  },
} as const;

// ── Payments v1: promotion (paid placement) copy ──────────────────────────────

/**
 * The one place the promotion prices live, so the card, the buy buttons, and any
 * paywall copy stay byte-identical. Boost is a one-time $100 / 30-day placement;
 * priority is a $50/mo recurring subscription until cancelled. Paid placement is
 * always DISCLOSED as such — never dressed up as an editorial/quality signal.
 */
export const PROMOTION_PRICING = {
  /** One-time boost: $100 for a 30-day featured window. */
  boostPrice: '$100',
  boostDuration: '30 days',
  /** Recurring priority placement: $50/mo until cancelled. */
  priorityPrice: '$50/mo',
} as const;

/** Verbatim promotion-card copy (payments v1). */
export const PROMOTION_COPY = {
  /** Heading when nothing is currently featured (the buy affordance). */
  offerTitle: 'Feature this project',
  offerBody: 'Pin your listing to the top of the directory. Paid placement is labelled “Featured” to investors.',
  /** Heading when a boost / priority placement is genuinely live in the directory. */
  activeTitle: 'Featured placement',
  /**
   * Heading when the only placement is a lapsed one (a `past_due` priority) — it is NOT
   * live in the directory (which features `status='active'` only), so it must never be
   * titled "Featured placement" (C23a). Paused, with the payment-issue rows below.
   */
  pausedTitle: 'Placement paused',
  boostBuyCta: `Feature — ${PROMOTION_PRICING.boostPrice} · ${PROMOTION_PRICING.boostDuration}`,
  boostExtendCta: `Extend ${PROMOTION_PRICING.boostDuration} — ${PROMOTION_PRICING.boostPrice}`,
  priorityBuyCta: `Priority — ${PROMOTION_PRICING.priorityPrice}`,
  /** Left-hand descriptors for the two buy rows (neither placement live yet). */
  boostOfferLabel: 'One-time 30-day boost',
  priorityOfferLabel: 'Recurring priority placement',
  /** `{n}` comes only from a real `endsAt` countdown — never fabricated. */
  boostActiveLabel: (daysLeft: number) =>
    `Featured · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
  /** The recurring-placement live line; `{date}` from `currentPeriodEnd` only. */
  priorityRenewsLabel: (date: string) =>
    date ? `Featured Priority · renews ${date}` : 'Featured Priority',
  priorityCancelsLabel: (date: string) =>
    date ? `Featured Priority · cancels on ${date}` : 'Featured Priority · cancelling',
  /**
   * C23a — a `past_due` priority is NOT live in the directory, so this label must never
   * assert "Featured". It reads as a paused placement with a payment issue, and points
   * the owner at the billing portal (via the Manage button) to fix it.
   */
  priorityPastDueLabel: 'Priority placement paused — payment issue',
  /** The lapsed-placement note pointing at the Stripe-hosted billing portal (C23a). */
  priorityPastDueNote:
    'Your priority placement is paused because a payment failed. Update your payment method in the billing portal to resume it.',
  /** The cancel note pointing at the Stripe-hosted billing portal. */
  priorityManageNote: 'Manage or cancel your priority placement in the billing portal.',
  /**
   * C25 — the checkout click returned 403 (ownership was revoked between the GET that
   * said we owned this listing and the click). Surfaced inline (never a silent dead
   * button, never a suppressed toast) so the owner learns why the action can't proceed.
   */
  ownershipRevoked: 'You no longer own this listing, so it can’t be featured.',
  notConfigured: 'Paid placement isn’t enabled on this environment yet.',
  unavailable: 'Couldn’t load placement options right now.',
} as const;

/** One UTC day in milliseconds (boost countdown granularity). */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days remaining in a live boost window, `ceil((endsAt - now)/day)`, floored at
 * 0. Pure over `{ endsAt, now }` so the card renders a deterministic, honest countdown
 * (a window 25h out reads "2 days left"; an already-past / unparseable end reads 0). A
 * server-active boost with a null/invalid `endsAt` yields 0 — the caller then omits the
 * countdown clause rather than inventing one.
 */
export function promotionBoostDaysLeft(
  endsAt: string | null | undefined,
  now: number = Date.now(),
): number {
  if (typeof endsAt !== 'string' || endsAt.length === 0) return 0;
  const endMs = Date.parse(endsAt);
  if (!Number.isFinite(endMs)) return 0;
  const remaining = endMs - now;
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / DAY_MS);
}
